// A chronicle flattened for drawing: every age with its depth, every event and life with the ages it sits in.

import type { Chronicle, ChronicleEvent, Period, Person } from "@hoee/schema";
import { whenOf } from "./dates.ts";

export interface Age {
  period: Period;
  depth: number;
  parent: Age | null;
  /** Its place among its siblings, for colouring. */
  order: number;
}

export interface Happening {
  event: ChronicleEvent;
  /** The year with its date folded in, for placing. */
  at: number;
  /** The deepest age it belongs to. */
  age: Age | null;
}

/** How much of a life the record gives: both ends, one of them, or only a time they were active. */
export type Known = "both" | "born" | "died" | "floruit";

export interface Life {
  person: Person;
  /** The years the record gives (equal when only one is known). */
  from: number;
  to: number;
  known: Known;
  /**
   * Where the life is drawn, including the faded part that stands for what is not known: a stretch after a
   * birth, before a death, or around a floruit. It shows uncertainty; it never claims a date.
   */
  reach: [number, number];
  age: Age | null;
}

/** How far a faded end reaches, in years: about a working life either side of what is known. */
export const UNKNOWN_REACH = { floruit: 15, open: 40 } as const;

export function lifeOf(p: Person, age: Age | null): Life | null {
  const { born, died, floruit } = p;
  if (born != null && died != null)
    return {
      person: p,
      from: born,
      to: Math.max(died, born + 1),
      known: "both",
      reach: [born, Math.max(died, born + 1)],
      age,
    };
  if (born != null)
    return { person: p, from: born, to: born, known: "born", reach: [born, born + UNKNOWN_REACH.open], age };
  if (died != null)
    return { person: p, from: died, to: died, known: "died", reach: [died - UNKNOWN_REACH.open, died], age };
  if (floruit != null)
    return {
      person: p,
      from: floruit,
      to: floruit,
      known: "floruit",
      reach: [floruit - UNKNOWN_REACH.floruit, floruit + UNKNOWN_REACH.floruit],
      age,
    };
  return null;
}

export interface Pages {
  ages: Age[];
  happenings: Happening[];
  lives: Life[];
  depth: number;
}

export function pagesOf(chronicle: Chronicle): Pages {
  const ages: Age[] = [];
  const happenings: Happening[] = [];
  const lives: Life[] = [];
  const walk = (node: Chronicle | Period, parent: Age | null, depth: number) => {
    for (const ev of node.events ?? []) happenings.push({ event: ev, at: whenOf(ev), age: parent });
    for (const p of node.people ?? []) {
      const life = lifeOf(p, parent);
      if (life) lives.push(life);
    }
    (node.periods ?? []).forEach((period, order) => {
      const age: Age = { period, depth, parent, order };
      ages.push(age);
      walk(period, age, depth + 1);
    });
  };
  walk(chronicle, null, 0);
  happenings.sort((a, b) => a.at - b.at);
  lives.sort((a, b) => a.reach[0] - b.reach[0]);
  return { ages, happenings, lives, depth: Math.max(0, ...ages.map((a) => a.depth + 1)) };
}

/**
 * Where you are when the years from..to are on screen: the deepest age holding the middle year that is long
 * enough to matter at this zoom (at least two fifths of the view), with all its parents, outermost first.
 */
export function agesAt(ages: readonly Age[], from: number, to: number): Age[] {
  const span = to - from,
    mid = (from + to) / 2;
  let deepest: Age | null = null;
  for (const age of ages) {
    const { from: a, to: b } = age.period;
    if (a <= mid && mid < b && b - a >= 0.4 * span && (!deepest || age.depth > deepest.depth)) deepest = age;
  }
  const trail: Age[] = [];
  for (let a = deepest; a; a = a.parent) trail.unshift(a);
  return trail;
}

/**
 * Rows for lives, most important first, each in the first row where it does not overlap another (with a little
 * room between). Computed from years alone, so nobody moves as you scroll or zoom. The rows that do not fit on
 * screen are then the least important people, never the most.
 */
export function lifeRows(lives: readonly Life[], gap = 2): Map<Life, number> {
  const rows: [number, number][][] = [];
  const out = new Map<Life, number>();
  const order = [...lives].sort((a, b) => a.person.importance - b.person.importance || a.reach[0] - b.reach[0]);
  for (const life of order) {
    const [a, b] = life.reach;
    let i = rows.findIndex((row) => row.every(([p, q]) => b + gap <= p || q + gap <= a));
    if (i < 0) {
      i = rows.length;
      rows.push([]);
    }
    (rows[i] as [number, number][]).push([a, b]);
    out.set(life, i);
  }
  return out;
}
